import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import ServicePage from './ServicePage';
import PremiumServicePage from './PremiumServicePage';
import WelcomeOverlay from './WelcomePage';
import PaymentFailedOverlay from './PaymentFailedOverlay';

// Smart API URL switching
const API_BASE_URL =
    window.location.hostname === "staging187611.digassistant.xyz"
        ? "https://api.digassistant.xyz"
        : window.location.hostname === "digassistant.xyz" || window.location.hostname === "www.digassistant.xyz"
            ? "https://api.digassistant.xyz"
            : "http://localhost:8080";

const isMobile = () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const ProtectedServiceRoute = () => {
    const [authData, setAuthData] = useState(null); // Will contain { authenticated, isPremium }
    const [loading, setLoading] = useState(true);

    // console.log('🔍 ProtectedServiceRoute: Component mounted');

    useEffect(() => {
        const checkAuthAndPremium = async () => {
            // console.log('🔍 ProtectedServiceRoute: Checking authentication and premium status...');
            try {
                const res = await fetch(`${API_BASE_URL}/auth/check`, {
                    credentials: "include", // Send cookies (JWT)
                });

                const data = await res.json();
                // console.log('✅ ProtectedServiceRoute: Auth response:', data);
                // console.log('✅ ProtectedServiceRoute: firstLogin =', data.firstLogin);
                setAuthData(data);
            } catch (error) {
                console.error("❌ Authentication check failed:", error);
                setAuthData({ authenticated: false, isPremium: false });
            } finally {
                setLoading(false);
            }
        };

        checkAuthAndPremium();
    }, []);

    // Clear paymentFailedShown flag if payment is no longer failed (user fixed it)
    const paymentFailed = authData?.paymentFailed === true;
    const hasShownPaymentFailed = localStorage.getItem('paymentFailedShown') === 'true';
    
    useEffect(() => {
        if (!paymentFailed && hasShownPaymentFailed) {
            localStorage.removeItem('paymentFailedShown');
        }
    }, [paymentFailed, hasShownPaymentFailed]);

    // console.log('ProtectedServiceRoute: loading =', loading, 'authData =', authData);

    if (loading) {
        // console.log('ProtectedServiceRoute: Showing loading state');
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontSize: '18px',
                color: '#4CAF50'
            }}>
                Loading...
            </div>
        );
    }

    // Check if user is authenticated but not verified
    if (authData?.verified === false) {
        // console.log('ProtectedServiceRoute: User not verified, redirecting to /verify');
        return <Navigate to="/verify" />;
    }

    if (!authData?.authenticated) {
        // console.log('ProtectedServiceRoute: Not authenticated, redirecting to /login');
        return <Navigate to="/login" />;
    }

    // console.log('ProtectedServiceRoute: Authenticated, routing based on premium status and first login');
    // console.log('ProtectedServiceRoute: authData.firstLogin =', authData?.firstLogin);
    // console.log('ProtectedServiceRoute: authData keys =', Object.keys(authData || {}));

    const fromVoyage = new URLSearchParams(window.location.search).get('from') === 'voyage';
    if (isMobile() && !fromVoyage) return <Navigate to="/search" />;

    // Route to appropriate service page based on premium status
    // Show welcome overlay if first login AND not premium AND hasn't been shown in this session
    const isFirstLogin = authData?.firstLogin === true;
    const isNotPremium = !authData?.isPremium;
    const hasShownWelcome = localStorage.getItem('welcomeShown') === 'true';
    const showWelcome = isFirstLogin && isNotPremium && !hasShownWelcome;

    // Show payment failed popup if payment failed AND hasn't been shown yet
    const showPaymentFailed = paymentFailed && !hasShownPaymentFailed;

    if (authData?.isPremium) {
        return (
            <>
                <PremiumServicePage />
                {showPaymentFailed && <PaymentFailedOverlay />}
            </>
        );
    } else {
        return (
            <>
                <ServicePage />
                {showWelcome && <WelcomeOverlay />}
                {showPaymentFailed && <PaymentFailedOverlay />}
            </>
        );
    }
};

export default ProtectedServiceRoute;
